/**
 * Shared category-aware tax-rate helper (v0.14.4 R1).
 *
 * Single source of truth for the per-line default GST rate. Promoted out of
 * AddItemModal.tsx (where it was private) so every invoice-item creation path
 * — AddItemModal, InvoiceCreatePage pre-selected items, BillingSessionPage
 * bulk-create — derives the default the same way instead of hardcoding 5%.
 *
 * R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC9/AC10): the suggested rate for a
 * revenue category is the TENANT'S CONFIGURED RATE (settings invoice_defaults
 * tax_rate), not this module's constant. DEFAULT_GST_RATE remains only as the
 * fallback when the tenant's configured rate has not been fetched (or the
 * fetch failed) — the same fallback BillingSessionPage always used.
 *
 * Rules (mirror the backend router + models/billable_items.py):
 *   - professional_fee / add_service / renewal → the tenant rate (revenue
 *     categories)
 *   - billable_expense / pass_through / anything else → 0% (not revenue)
 *
 * This is the FRONTEND suggestion. The backend applies its own category-aware
 * default for the omitted case (routers/invoice_items.py), and an explicit
 * tax_rate always wins server-side. The human can still edit any line.
 */

/** Categories that carry the tenant GST rate by default. */
const REVENUE_CATEGORIES = new Set(['professional_fee', 'add_service', 'renewal']);

/** Fallback GST percentage when the tenant's configured rate is unknown. */
export const DEFAULT_GST_RATE = 5.0;

/**
 * Suggested default tax rate (percent) for a billable item's category.
 *
 * @param category - The billable item category (may be null/undefined/unknown).
 * @param tenantRate - The tenant's configured tax rate (invoice_defaults.tax_rate),
 *   or null/undefined when not (yet) known. When absent the module fallback
 *   applies — never a hardcoded assumption that overrides tenant config.
 * @returns The configured rate for revenue categories, 0.0 otherwise.
 */
export function getSuggestedTaxRate(
  category: string | null | undefined,
  tenantRate?: number | null
): number {
  const rate =
    typeof tenantRate === 'number' && Number.isFinite(tenantRate) && tenantRate >= 0
      ? tenantRate
      : DEFAULT_GST_RATE;
  return category && REVENUE_CATEGORIES.has(category) ? rate : 0.0;
}
